import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import { validationDocumentationSliceAction } from "../../slices/validation-documentation";
import {
  validationDocumentationSagaActionTypes,
  GetValidationDocumentationPayloadType,
  SaveValidationDocumentationPayloadType,
  SubmitValidationDocumentationPayloadType,
  GetValidationHistoryPayloadType,
  GetValidationHistoryDetailPayloadType,
  getValidationHistorySagaAction,
  FinalSubmitValidationDocumentationPayloadType,
  getValidationHistoryDetailSagaAction,
  getValidationDocumentationSagaAction,
  GetValidationSummaryPayloadType,
  GetRecentHistoryDocumentationPayloadType,
  ExportHistoryValidationReportPayloadType,
  DownloadSolutionCertificatePayloadType,
} from "./saga-actions";
import {
  DocumentationResponse,
  SolutionCertificateResponse,
} from "../../slices/validation-documentation/Types";
import { HistoryEntryType, ValidationSummaryType } from "../../slices/validation-documentation/index";
import {
  mapSolutionCertificateResponse,
  openSolutionCertificateInTab,
} from "../../../pages/partner/dashboard/projects/project-details/validation/components/SolutionCertificatePDF";

const getFileNameFromContentDisposition = (
  contentDisposition: string | undefined,
  fallback = "history-validation-report.xlsx"
): string => {
  if (!contentDisposition) return fallback;
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(contentDisposition);
  if (!match) return fallback;
  const raw = match[1].replace(/['"]/g, "");
  try {
    return decodeURIComponent(raw) || fallback;
  } catch {
    return raw || fallback;
  }
};

// API Function
const getValidationDocumentationAPI = async (
  technology: string,
  integrationType: string,
  integrationId: number
) => {
  const params = new URLSearchParams({
    technology: technology,
    integration_type: integrationType,
    integration_id: integrationId.toString(),
  });

  const response = await axiosRestInstance.get<
    AxiosResponse<DocumentationResponse>
  >(`/project/integration-validation/documentation/?${params.toString()}`);
  return response.data;
};

const exportHistoryValidationReportAPI = async (
  integrationId: number,
  documentationId: number,
  partnerName?: string,
  projectName?: string
) => {
  const params = new URLSearchParams({
    integration_id: integrationId.toString(),
    documentation_id: documentationId.toString(),
  });
  if (partnerName) {
  			params.append("partner_name", partnerName);
	}
	if (projectName) {
  			params.append("project_name", projectName);
	}
  return axiosRestInstance.get<Blob>(
    `/project/export/history-validation-report/?${params.toString()}`,
    { responseType: "blob" }
  );
};

const getSolutionCertificateAPI = async (
  integrationId: number,
  documentationId: number
) => {
  const params = new URLSearchParams({
    documentation_id: documentationId.toString(),
    integration_id: integrationId.toString(),
  });

  const response = await axiosRestInstance.get<SolutionCertificateResponse>(
    `/project/certificateDownload/?${params.toString()}`
  );
  return response.data;
};

// API Function for History Detail
const getValidationHistoryDetailAPI = async (
  integrationId: number,
  documentationId: number
) => {
  const params = new URLSearchParams({
    integration_id: integrationId.toString(),
    documentation_id: documentationId.toString(),
  });

  const response = await axiosRestInstance.get<
    AxiosResponse<DocumentationResponse>
  >(
    `/project/integration-validation/view/history?${params.toString()}`
  );
  return response.data;
};

// API Function for Save
const saveValidationDocumentationAPI = async (
  payload: SaveValidationDocumentationPayloadType
) => {
  const response = await axiosRestInstance.post<
    AxiosResponse<{ success: boolean; message?: string }>
  >(`/project/integration-validation/documentation/save`, payload);
  return response.data;
};

// API Function for Submit
// TEMPORARY: created_at from data-entry "Submitted on" (YYYY-MM-DD). Remove with TEMPORARY block.
const submitValidationDocumentationAPI = async (
  integrationId: number,
  user: number,
  created_at?: string
) => {
  const body: { integration_id: number; user: number; created_at?: string } = {
    integration_id: integrationId,
    user: user,
  };
  if (created_at) body.created_at = created_at;
  const response = await axiosRestInstance.post<
    AxiosResponse<{ success: boolean; message?: string }>
  >(`/project/integration-validation/documentation/submit`, body);
  return response.data;
};

// API Function for History
const getValidationHistoryAPI = async (integrationId: number) => {
  const response = await axiosRestInstance.get<
    AxiosResponse<{
      success: boolean;
      data?: HistoryEntryType[];
      message?: string;
    }>
  >(`/project/integration-validation/history/${integrationId}`);
  return response.data;
};

// API Function for Summary
const getValidationSummaryAPI = async (integrationId: number, documentationId: number) => {
  const params = new URLSearchParams({
    integration_id: integrationId.toString(),
    documentation_id: documentationId.toString(),
  });

  const response = await axiosRestInstance.get<
    AxiosResponse<{
      success: boolean;
      integration_id: string;
      documentation_id: number;
      scores: {
        mandatory: number;
        recommended: number;
        bestPractice: number;
        optional: number;
      };
      overall_score: number;
      endorsement_level: string;
      message?: string;
    }>
  >(`/project/integration-validation/summary/?${params.toString()}`);
  return response.data;
};

// Saga Function
function* getValidationDocumentationSaga(action: {
  type: string;
  payload: GetValidationDocumentationPayloadType;
}) {
  try {
    yield put(validationDocumentationSliceAction.setDocumentationLoading(true));
    yield put(validationDocumentationSliceAction.setDocumentationError(null));

    const response: DocumentationResponse = yield call(
      getValidationDocumentationAPI,
      action.payload.technology,
      action.payload.integrationType,
      action.payload.integrationId
    );

    if (response.success) {
      // Enrich categories with root-level fields when API returns new structure
      // (notes/validation_status moved from per-category to root)
      const rawData = response.data || [];
      const rootNotes = {
        hid_notes: response.hid_notes,
        partner_notes: response.partner_notes,
        validation_status: response.validation_status,
        integration_id: response.integration_id,
      };
      const enrichedData = rawData.map((cat) => ({
        ...cat,
        hid_notes: cat.hid_notes ?? rootNotes.hid_notes ?? null,
        partner_notes: cat.partner_notes ?? rootNotes.partner_notes ?? null,
        validation_status: cat.validation_status ?? rootNotes.validation_status ?? null,
        integration_id: cat.integration_id ?? rootNotes.integration_id ?? null,
      }));
      yield put(validationDocumentationSliceAction.setDocumentationData(enrichedData));
      yield put(validationDocumentationSliceAction.setCurrentDocumentationId(response.documentation_id));
      yield put(
        validationDocumentationSliceAction.setCurrentValidationStatus(
          response.validation_status ?? null
        )
      );
      yield put(validationDocumentationSliceAction.setSubmittedBy(response.submitted_by ?? null));
      yield put(validationDocumentationSliceAction.setSubmittedByAdmin(response.submitted_by_admin ?? null));
    } else {
      yield put(
        validationDocumentationSliceAction.setDocumentationError(
          "Failed to fetch validation documentation"
        )
      );
      yield put(validationDocumentationSliceAction.setDocumentationData([]));
      yield put(validationDocumentationSliceAction.setCurrentDocumentationId(null));
      yield put(validationDocumentationSliceAction.setCurrentValidationStatus(null));
    }
  } catch (e: any) {
    console.error("Error fetching validation documentation:", e);
    yield put(
      validationDocumentationSliceAction.setDocumentationError(
        e.response?.data?.message || "Failed to fetch validation documentation"
      )
    );
    yield put(validationDocumentationSliceAction.setDocumentationData([]));
    yield put(validationDocumentationSliceAction.setCurrentDocumentationId(null));
    yield put(validationDocumentationSliceAction.setCurrentValidationStatus(null));
  } finally {
    yield put(
      validationDocumentationSliceAction.setDocumentationLoading(false)
    );
  }
}

// Saga Function for Save
function* saveValidationDocumentationSaga(action: {
  type: string;
  payload: SaveValidationDocumentationPayloadType;
}) {
  const isHistoryView = action.payload.isHistoryView || false;
  
  try {
    // Use appropriate loading state based on context
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailLoading(true));
      yield put(validationDocumentationSliceAction.setHistoryDetailError(null));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationLoading(true));
      yield put(validationDocumentationSliceAction.setDocumentationError(null));
    }

    const response: { success: boolean; message?: string } = yield call(
      saveValidationDocumentationAPI,
      action.payload
    );

    if (response.success) {
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(null));
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(null));
        // Refresh documentation data for regular view
        yield call(getValidationDocumentationSaga, {
          type: validationDocumentationSagaActionTypes.callGetValidationDocumentationSaga,
          payload: {
            technology: action.payload.technology,
            integrationType: action.payload.integration_type,
            integrationId: action.payload.integration_id,
          },
        });
      }
    } else {
      const errorMessage = response.message || "Failed to save validation documentation";
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
      }
    }
  } catch (e: any) {
    console.error("Error saving validation documentation:", e);
    const errorMessage = e.response?.data?.message || "Failed to save validation documentation";
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
    }
  } finally {
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailLoading(false));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationLoading(false));
    }
  }
}

// Saga Function for Submit
function* submitValidationDocumentationSaga(action: {
  type: string;
  payload: SubmitValidationDocumentationPayloadType;
}) {
  const isHistoryView = action.payload.savePayload.isHistoryView || false;
  
  try {
    // Use appropriate loading state based on context
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailLoading(true));
      yield put(validationDocumentationSliceAction.setHistoryDetailError(null));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationLoading(true));
      yield put(validationDocumentationSliceAction.setDocumentationError(null));
    }

    // First, save documentation with validation_status
    const saveResponse: { success: boolean; message?: string } = yield call(
      saveValidationDocumentationAPI,
      action.payload.savePayload
    );

    if (!saveResponse.success) {
      const errorMessage = saveResponse.message || "Failed to save validation documentation before submit";
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
      }
      return;
    }

    // Then, call submit API (integration_id, user, and optional created_at - TEMPORARY)
    const submitResponse: { success: boolean; message?: string } = yield call(
      submitValidationDocumentationAPI,
      action.payload.integration_id,
      action.payload.user,
      action.payload.created_at
    );

    if (submitResponse.success) {
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(null));
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(null));
        // Refresh documentation data for regular view
        yield put(
          getValidationDocumentationSagaAction(
            action.payload.savePayload.technology,
            action.payload.savePayload.integration_type,
            action.payload.savePayload.integration_id
          )
        );
      }
      yield put(getValidationHistorySagaAction(action.payload.savePayload.integration_id));
    } else {
      const errorMessage = submitResponse.message || "Failed to submit validation documentation";
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
      }
    }
  } catch (e: any) {
    console.error("Error submitting validation documentation:", e);
    const errorMessage = e.response?.data?.message || "Failed to submit validation documentation";
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
    }
  } finally {
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailLoading(false));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationLoading(false));
    }
  }
}

function* finalSubmitValidationDocumentationSaga(action: {
  type: string;
  payload: FinalSubmitValidationDocumentationPayloadType;
}) {
  const isHistoryView = action.payload.savePayload.isHistoryView || false;
  
  try {
    // Use appropriate loading state based on context
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailLoading(true));
      yield put(validationDocumentationSliceAction.setHistoryDetailError(null));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationLoading(true));
      yield put(validationDocumentationSliceAction.setDocumentationError(null));
    }

    // First, save documentation with validation_status
    const saveResponse: { success: boolean; message?: string } = yield call(
      saveValidationDocumentationAPI,
      action.payload.savePayload
    );

    if (!saveResponse.success) {
      const errorMessage = saveResponse.message || "Failed to save validation documentation before submit";
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
      }
      return;
    }

    // Then, call submit API (integration_id, user, and optional created_at)
    const submitResponse: { success: boolean; message?: string } = yield call(
      submitValidationDocumentationAPI,
      action.payload.integration_id,
      action.payload.user,
      action.payload.created_at
    );

    if (submitResponse.success) {
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(null));
        // Refresh history detail data after successful submit
        yield put(
          getValidationHistoryDetailSagaAction(
            action.payload.integration_id,
            action.payload.documentation_id
          )
        );
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(null));
      }
      yield put(getValidationHistorySagaAction(action.payload.integration_id));
    } else {
      const errorMessage = submitResponse.message || "Failed to submit validation documentation";
      if (isHistoryView) {
        yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
      } else {
        yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
      }
    }
  } catch (e: any) {
    console.error("Error submitting validation documentation:", e);
    const errorMessage = e.response?.data?.message || "Failed to submit validation documentation";
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailError(errorMessage));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationError(errorMessage));
    }
  } finally {
    if (isHistoryView) {
      yield put(validationDocumentationSliceAction.setHistoryDetailLoading(false));
    } else {
      yield put(validationDocumentationSliceAction.setDocumentationLoading(false));
    }
  }
}

// Saga Function for History
function* getValidationHistorySaga(action: {
  type: string;
  payload: GetValidationHistoryPayloadType;
}) {
  try {
    yield put(validationDocumentationSliceAction.setHistoryLoading(true));
    yield put(validationDocumentationSliceAction.setHistoryError(null));

    const response: {
      success: boolean;
      data?: HistoryEntryType[];
      message?: string;
    } = yield call(getValidationHistoryAPI, action.payload.integration_id);

    if (response.success) {
      yield put(
        validationDocumentationSliceAction.setHistoryData(response.data || [])
      );
    } else {
      yield put(
        validationDocumentationSliceAction.setHistoryError(
          response.message || "Failed to fetch validation history"
        )
      );
      yield put(validationDocumentationSliceAction.setHistoryData([]));
    }
  } catch (e: any) {
    console.error("Error fetching validation history:", e);
    yield put(
      validationDocumentationSliceAction.setHistoryError(
        e.response?.data?.message || "Failed to fetch validation history"
      )
    );
    yield put(validationDocumentationSliceAction.setHistoryData([]));
  } finally {
    yield put(validationDocumentationSliceAction.setHistoryLoading(false));
  }
}

// Saga Function for History Detail
function* getValidationHistoryDetailSaga(action: {
  type: string;
  payload: GetValidationHistoryDetailPayloadType;
}) {
  try {
    yield put(validationDocumentationSliceAction.setHistoryDetailLoading(true));
    yield put(validationDocumentationSliceAction.setHistoryDetailError(null));

    const response: DocumentationResponse = yield call(
      getValidationHistoryDetailAPI,
      action.payload.integration_id,
      action.payload.documentation_id
    );

    if (response.success) {
      const rawData = response.data || [];
      const enrichedData = rawData.map((cat) => ({
        ...cat,
        submitted_by: cat.submitted_by ?? response.submitted_by ?? null,
        submitted_by_admin: cat.submitted_by_admin ?? response.submitted_by_admin ?? null,
        partner_submission_date:
          cat.partner_submission_date ??
          response.partner_submission_date ??
          null,
        admin_submission_date:
          cat.admin_submission_date ??
          response.admin_submission_date ??
          null,
      }));
      yield put(
        validationDocumentationSliceAction.setHistoryDetailDocumentation(enrichedData)
      );
      
      // Extract submitted_by from first category (they're the same across all categories)
      const firstCategory = enrichedData.length > 0 ? enrichedData[0] : null;
      const submittedBy = firstCategory?.submitted_by ?? response.submitted_by ?? null;
      const submittedByAdmin = firstCategory?.submitted_by_admin ?? response.submitted_by_admin ?? null;
      
      yield put(validationDocumentationSliceAction.setSubmittedBy(submittedBy));
      yield put(validationDocumentationSliceAction.setSubmittedByAdmin(submittedByAdmin));
    } else {
      yield put(
        validationDocumentationSliceAction.setHistoryDetailError(
          "Failed to fetch validation history detail"
        )
      );
      yield put(validationDocumentationSliceAction.setHistoryDetailDocumentation([]));
    }
  } catch (e: any) {
    console.error("Error fetching validation history detail:", e);
    yield put(
      validationDocumentationSliceAction.setHistoryDetailError(
        e.response?.data?.message || "Failed to fetch validation history detail"
      )
    );
    yield put(validationDocumentationSliceAction.setHistoryDetailDocumentation([]));
  } finally {
    yield put(
      validationDocumentationSliceAction.setHistoryDetailLoading(false)
    );
  }
}

// Saga Function for Recent History Documentation
function* getRecentHistoryDocumentationSaga(action: {
  type: string;
  payload: GetRecentHistoryDocumentationPayloadType;
}) {
  try {
    yield put(validationDocumentationSliceAction.setRecentHistoryLoading(true));
    yield put(validationDocumentationSliceAction.setRecentHistoryError(null));

    const response: DocumentationResponse = yield call(
      getValidationHistoryDetailAPI,
      action.payload.integration_id,
      action.payload.documentation_id
    );

    if (response.success) {
      const rawData = response.data || [];
      const enrichedData = rawData.map((cat) => ({
        ...cat,
        submitted_by: cat.submitted_by ?? response.submitted_by ?? null,
        submitted_by_admin: cat.submitted_by_admin ?? response.submitted_by_admin ?? null,
        partner_submission_date:
          cat.partner_submission_date ??
          response.partner_submission_date ??
          null,
        admin_submission_date:
          cat.admin_submission_date ??
          response.admin_submission_date ??
          null,
      }));
      yield put(
        validationDocumentationSliceAction.setRecentHistoryDocumentation(
          enrichedData
        )
      );
    } else {
      yield put(
        validationDocumentationSliceAction.setRecentHistoryError(
          "Failed to fetch recent history documentation"
        )
      );
      yield put(validationDocumentationSliceAction.setRecentHistoryDocumentation(null));
    }
  } catch (e: any) {
    console.error("Error fetching recent history documentation:", e);
    yield put(
      validationDocumentationSliceAction.setRecentHistoryError(
        e.response?.data?.message || "Failed to fetch recent history documentation"
      )
    );
    yield put(validationDocumentationSliceAction.setRecentHistoryDocumentation(null));
  } finally {
    yield put(validationDocumentationSliceAction.setRecentHistoryLoading(false));
  }
}

// Saga Function for Summary
function* getValidationSummarySaga(action: {
  type: string;
  payload: GetValidationSummaryPayloadType;
}) {
  try {
    yield put(validationDocumentationSliceAction.setSummaryLoading(true));
    yield put(validationDocumentationSliceAction.setSummaryError(null));

    const response: {
      success: boolean;
      integration_id: string;
      documentation_id: number;
      scores: {
        mandatory: number;
        recommended: number;
        bestPractice: number;
        optional: number;
      };
      overall_score: number;
      total_validation_score: number;
      endorsement_level: string;
      message?: string;
    } = yield call(getValidationSummaryAPI, action.payload.integration_id, action.payload.documentation_id);

    if (response.success) {
      const summaryData: ValidationSummaryType = {
        scores: response.scores,
        overall_score: response.overall_score,
        total_validation_score: response.total_validation_score,
        endorsement_level: response.endorsement_level,
        integration_id: response.integration_id,
        documentation_id: response.documentation_id,
      };
      yield put(validationDocumentationSliceAction.setSummaryData(summaryData));
    } else {
      yield put(
        validationDocumentationSliceAction.setSummaryError(
          response.message || "Failed to fetch validation summary"
        )
      );
      yield put(validationDocumentationSliceAction.setSummaryData(null));
    }
  } catch (e: any) {
    console.error("Error fetching validation summary:", e);
    yield put(
      validationDocumentationSliceAction.setSummaryError(
        e.response?.data?.message || "Failed to fetch validation summary"
      )
    );
    yield put(validationDocumentationSliceAction.setSummaryData(null));
  } finally {
    yield put(validationDocumentationSliceAction.setSummaryLoading(false));
  }
}

function* exportHistoryValidationReportSaga(action: {
  type: string;
  payload: ExportHistoryValidationReportPayloadType;
}) {
  try {
    yield put(validationDocumentationSliceAction.setHistoryReportExportLoading(true));

    const response: AxiosResponse<Blob> = yield call(
      exportHistoryValidationReportAPI,
      action.payload.integration_id,
      action.payload.documentation_id,
      action.payload.partner_name,
      action.payload.project_name
    );

    const blob = response?.data;
    if (!blob || !(blob instanceof Blob)) {
      throw new Error("Invalid export response.");
    }

    const filename = getFileNameFromContentDisposition(
      response.headers?.["content-disposition"] ||
        response.headers?.["Content-Disposition"]
    );

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("History validation report export failed:", error);
  } finally {
    yield put(validationDocumentationSliceAction.setHistoryReportExportLoading(false));
  }
}

function* downloadSolutionCertificateSaga(action: {
  type: string;
  payload: DownloadSolutionCertificatePayloadType;
}) {
  // Must stay before the first `yield`: this still runs inside the click's call stack,
  // so the browser treats it as user-initiated and doesn't block the popup.
  const certificateTab = window.open("", "_blank");
  try {
    yield put(validationDocumentationSliceAction.setCertificateDownloadLoading(true));

    const response: SolutionCertificateResponse = yield call(
      getSolutionCertificateAPI,
      action.payload.integration_id,
      action.payload.documentation_id
    );

    if (!response?.success || !response.data) {
      throw new Error("Invalid certificate response.");
    }

    yield call(
      openSolutionCertificateInTab,
      mapSolutionCertificateResponse(response.data, action.payload.company_name),
      certificateTab
    );
  } catch (error) {
    console.error("Solution certificate download failed:", error);
    certificateTab?.close();
  } finally {
    yield put(validationDocumentationSliceAction.setCertificateDownloadLoading(false));
  }
}

// Root Saga
function* validationDocumentationSaga() {
  yield takeLatest(
    validationDocumentationSagaActionTypes.callGetValidationDocumentationSaga,
    getValidationDocumentationSaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callSaveValidationDocumentationSaga,
    saveValidationDocumentationSaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callSubmitValidationDocumentationSaga,
    submitValidationDocumentationSaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callGetValidationHistorySaga,
    getValidationHistorySaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callGetValidationHistoryDetailSaga,
    getValidationHistoryDetailSaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callFinalSubmitValidationDocumentationSaga,
    finalSubmitValidationDocumentationSaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callGetValidationSummarySaga,
    getValidationSummarySaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callGetRecentHistoryDocumentationSaga,
    getRecentHistoryDocumentationSaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callExportHistoryValidationReportSaga,
    exportHistoryValidationReportSaga
  );
  yield takeLatest(
    validationDocumentationSagaActionTypes.callDownloadSolutionCertificateSaga,
    downloadSolutionCertificateSaga
  );
}

export default validationDocumentationSaga;
